import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import H2 from '@base/H2/H2';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import { fetchTopologyFreeCourseFeed } from '@actions/topologies';
import breadcrumbs from '@utils/breadcrumbs';
import Topology from '@models/Topology';
import { PlusTopologySource } from '../../../constants';
import FreeTopicFeedList from './FreeTopicFeedList';
import FreeTopicCourses from './FreeTopicCourses';
import { Spacings } from '../../../styleConstants';
import FreeTopicList from './FreeTopicList';

const HeaderNavBar = styled(Paper)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_12B};
`;

const Title = styled(H2)`
  margin-top: ${Spacings.SPACING_6B};
  color: var(--color-text-primary);
`;

const FreeTopology = ({ source, dataAnalytics }) => {
  const router = useRouter();
  let { goalUID, topicSlugUID: topicUID } = router.query;
  if (source === PlusTopologySource.TOPIC_COURSES) {
    topicUID = router.query.conceptUID;
  }
  const dispatch = useDispatchToStore();
  const mapStateToProps = ({ topologies, paginator }) => ({
    goalTopology: topologies.topologyDetails?.[goalUID],
    topicTopology: topicUID ? topologies.topologyDetails?.[topicUID] : null,
    topologyChildren: topologies.freeTopologyChildren?.[topicUID]?.results,
    topologyCourseFeed: paginator.topologyFreeCourseFeed?.[topicUID],
    freeCoursesOfAConcept: paginator.freeCoursesOfAConcept[topicUID]
  });

  const props = useSelectorToStore(mapStateToProps);

  const {
    goalTopology,
    topicTopology,
    topologyChildren,
    topologyCourseFeed,
    freeCoursesOfAConcept
  } = props;
  const onClick = () => {};
  const loadMoreFeedBlocks = () => {
    return dispatch(fetchTopologyFreeCourseFeed(topicUID, true));
  };

  const showChildren = () => {
    return (
      topologyChildren &&
      (source === PlusTopologySource.TOPICS ||
        source === PlusTopologySource.TOPIC_FEED)
    );
  };

  const showTopicFeed = () => {
    return (
      topologyCourseFeed &&
      (source === PlusTopologySource.TOPICS ||
        source === PlusTopologySource.TOPIC_FEED)
    );
  };

  const showTopicCourses = () => {
    return source === PlusTopologySource.TOPIC_COURSES;
  };

  const getHeaderTitle = () => {
    if (topicUID) {
      return topicTopology.name || '';
    }
    return goalTopology.name;
  };

  const getHeaderTags = () => {
    const headerTags = [];
    if (goalTopology) {
      headerTags.push(
        ...breadcrumbs.getGoalAndFreeTagsOfFreeTopic(goalTopology)
      );
    }
    const topicTags = [];
    let { parent } = topicTopology;
    while (parent !== null && parent.level !== 10) {
      const { uid, relative_link, name } = parent;
      const splitRelativeLink = relative_link.split('/');
      const tpcSlug = splitRelativeLink[splitRelativeLink.length - 2];
      topicTags.push({
        title: name,
        as: `${Topology.getLink(goalTopology)}/free-platform/${tpcSlug}/${uid}`,
        href: '/goal/[goalSlug]/[goalUID]/free-platform/[topicSlug]/[topicSlugUID]'
      });
      parent = parent.parent;
    }
    topicTags.reverse();
    headerTags.push(...topicTags);
    return headerTags;
  };

  return (
    <>
      <HeaderNavBar gridColumn="span 18">
        <BreadCrumbs tags={getHeaderTags()} onBackClick={breadcrumbs.goBack} />
        <Title as="h1">{getHeaderTitle()}</Title>
      </HeaderNavBar>
      {showChildren() && (
        <FreeTopicList
          dataAnalytics={dataAnalytics}
          topics={topologyChildren}
          goal={goalTopology}
          onClick={onClick}
        />
      )}
      {showTopicFeed() && (
        <FreeTopicFeedList
          dataAnalytics={dataAnalytics}
          topologyFeed={topologyCourseFeed}
          loadMore={loadMoreFeedBlocks}
          goalTopology={goalTopology}
          topologyChildren={topologyChildren}
        />
      )}
      {showTopicCourses() && (
        <FreeTopicCourses
          dataAnalytics={dataAnalytics}
          goalUID={goalUID}
          topicUid={topicUID}
          courseData={freeCoursesOfAConcept}
        />
      )}
    </>
  );
};

export default FreeTopology;

FreeTopology.propTypes = {
  goalTopology: PropTypes.objectOf(PropTypes.any),
  topicTopology: PropTypes.objectOf(PropTypes.any),
  topologyChildren: PropTypes.objectOf(PropTypes.any),
  topologyCourseFeed: PropTypes.objectOf(PropTypes.any),
  freeCoursesOfAConcept: PropTypes.objectOf(PropTypes.any),
  source: PropTypes.string.isRequired,
  dataAnalytics: PropTypes.string.isRequired
};

FreeTopology.defaultProps = {
  goalTopology: null,
  topicTopology: null,
  topologyChildren: null,
  topologyCourseFeed: null,
  freeCoursesOfAConcept: null
};
